import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation, getLanguage } from "@/i18n";
import { monthsFreeOnYearly } from "../../utils/planSavings";
import { trialMessage } from "../../utils/subscriptionState";
import { useSubscriptionStatus } from "../../hooks/useSubscriptionStatus";
import { formatApiDate } from "../../utils/dateLocal";

/**
 * PaywallModal - the Premium purchase screen.
 *
 * Prices are shown here as the planned launch prices; once RevenueCat is wired
 * (a later piece) they should come from the store's localised product objects
 * instead of these constants. The subscribe/restore actions are placeholders
 * until then - they explain that billing isn't live rather than pretending to
 * charge. Apple requires a visible "Restore purchases" control, so it's here
 * from the start.
 */
/**
 * THESE TABLES HOLD KEYS, NOT WORDS. (v18.50)
 *
 * They used to hold the English sentences themselves - "AI receipt scanning",
 * "Best value · about 2 months free" - and every one of them reached all 27
 * other languages in English. Emergent's browser found them on the German
 * paywall while every i18n checker in the gate was green.
 *
 * That is the third hiding place this month, after copy inside a component
 * and copy inside a brace. A locale-parity checker compares locale files; a
 * JSX checker reads text between a > and a <. A sentence sitting in a
 * module-level array is neither, and gets mapped straight onto the screen.
 *
 * The prices are still constants on purpose: they are the planned AUD launch
 * prices and are replaced by the store's own localised product objects when
 * RevenueCat is wired. It is a missing integration, not a missing
 * translation, and it is recorded in docs/launch-checklist.md.
 *
 * WHAT CHANGED 26 SEPTEMBER 2026 (v18.59)
 *
 *   Until today a German user read "A$29.99" with nothing beside it. Two
 *   things were wrong with that, and only one of them was the missing
 *   integration:
 *
 *     1. The money was unexplained. A price in a currency that is not yours,
 *        on the screen where you are asked to pay, with no word about what
 *        you will actually be charged. That is the one place in an app where
 *        vagueness is not a rough edge.
 *
 *     2. The currency was written into each price string, so "A$" appeared
 *        in two places and the number in two more. Whoever wires RevenueCat
 *        has to find all four.
 *
 *   Now: one currency constant, the amounts separate from it, and a line
 *   under the plans saying these are Australian dollars and the app store
 *   will show the price in your own money. Both true today, and the note
 *   disappears on its own once STORE_PRICED goes true.
 */

// The one place the launch currency is written. RevenueCat replaces these
// with the store's own localised product objects; flip STORE_PRICED and the
// "prices are in Australian dollars" note stops being shown.
const LAUNCH_CURRENCY = "A$";
const STORE_PRICED = false;

const PLANS = [
  { id: "yearly", titleKey: "premium.plan.yearly", amount: "29.99",
    perKey: "premium.plan.perYear", noteKey: "premium.plan.bestValue" },
  { id: "monthly", titleKey: "premium.plan.monthly", amount: "3.99",
    perKey: "premium.plan.perMonth", noteKey: null },
];

const PREMIUM_PERKS = [
  "premium.perk.receipts",
  "premium.perk.recipeImport",
  "premium.perk.health",
  "premium.perk.analytics",
  "premium.perk.unlimitedRecipes",
  "premium.perk.support",
];

export default function PaywallModal({ open, feature, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("yearly");
  // The trial, in words (V19.03, Ivan's decision on the full test, item 23).
  const trial = trialMessage(useSubscriptionStatus(open));

  if (!open) return null;

  const goLegal = (path) => { onClose(); navigate(path); };

  const subscribe = () => {
    // Placeholder until RevenueCat is wired.
    toast(t('premium.notLive'));
  };

  const restore = () => {
    toast(t('premium.nothingToRestore'));
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/60 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-bold">{t('premium.title')}</h2>
          <button onClick={onClose} aria-label={t("common.close")} data-testid="paywall-close" className="text-gray-400">
            <X size={22} />
          </button>
        </div>
        {trial.phase === 'trial' && trial.endsAt && (
          <p className="text-sm font-semibold text-orange-700 dark:text-orange-300 mb-2" data-testid="paywall-trial-line">
            {t('premium.trialEndsOn', { date: formatApiDate(trial.endsAt, { day: 'numeric', month: 'short' }) })}
          </p>
        )}
        {trial.phase === 'ended' && (
          <p className="text-sm font-semibold text-orange-700 dark:text-orange-300 mb-2" data-testid="paywall-trial-ended">
            {t('premium.trialEnded')}
          </p>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('premium.blurb')}</p>

        <ul className="space-y-2 mb-5">
          {PREMIUM_PERKS.map((perkKey) => (
            <li key={perkKey} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
              <Check size={18} className="mt-0.5 flex-shrink-0" />
              <span>{t(perkKey)}</span>
            </li>
          ))}
        </ul>

        <div className="space-y-2 mb-5">
          {PLANS.map((p) => {
            const selected = plan === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPlan(p.id)}
                data-testid={`paywall-plan-${p.id}`}
                className={`w-full text-left rounded-2xl p-4 border-2 transition-colors ${
                  selected
                    ? "border-black dark:border-white bg-gray-50 dark:bg-gray-800"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{t(p.titleKey)}</span>
                  <span className="font-bold">
                    {LAUNCH_CURRENCY}{p.amount}<span className="text-sm font-normal text-gray-500 dark:text-gray-400">{t(p.perKey)}</span>
                  </span>
                </div>
                {p.noteKey && monthsFreeOnYearly(PLANS) != null && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t(p.noteKey, { months: monthsFreeOnYearly(PLANS).toLocaleString(getLanguage() || undefined) })}</p>}
              </button>
            );
          })}
        </div>

        {/* The prices above are Australian dollars and the store has not
            priced this app locally yet. Saying so is the difference between
            an unfinished integration and a misleading price. */}
        {!STORE_PRICED && (
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3"
             data-testid="paywall-currency-note">
            {t('premium.pricesInAud')}
          </p>
        )}

        <button
          onClick={subscribe}
          data-testid="paywall-subscribe"
          className="w-full py-3 rounded-full bg-black text-white dark:bg-white dark:text-black font-semibold mb-2"
        >{t('premium.start')}</button>
        <button
          onClick={restore}
          data-testid="paywall-restore"
          className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 mb-4"
        >{t('premium.restore')}</button>

        <p className="text-[11px] leading-relaxed text-gray-400 dark:text-gray-500 text-center">
          {t('premium.trialSmallPrint')}{" "}
          {t('premium.renewNotice')}{" "}
          <button onClick={() => goLegal("/terms")} className="underline">{t('legal.termsShort')}</button>{" "}
          {t('common.and')}{" "}
          <button onClick={() => goLegal("/privacy")} className="underline">{t('legal.privacy')}</button>.
        </p>
      </div>
    </div>,
    document.body
  );
}
